// A/B Testing utilities

export interface ABTestVariant {
  name: string;
  weight: number;
  config?: Record<string, any>;
}

export interface ABTest {
  id: string;
  test_name: string;
  description?: string;
  variants: ABTestVariant[];
  status: 'draft' | 'active' | 'paused' | 'completed';
  start_date?: string;
  end_date?: string;
}

export function selectVariant(variants: ABTestVariant[], userId?: string): string {
  // Use userId for consistent assignment, or random for anonymous
  const seed = userId ? hashCode(userId) : Math.random();
  const random = userId ? (seed % 100) / 100 : seed;

  let cumulative = 0;
  for (const variant of variants) {
    cumulative += variant.weight;
    if (random < cumulative) {
      return variant.name;
    }
  }

  // Fallback to first variant
  return variants[0]?.name || 'control';
}

function hashCode(str: string): number {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    const char = str.charCodeAt(i);
    hash = (hash << 5) - hash + char;
    hash = hash & hash; // Convert to 32-bit integer
  }
  return Math.abs(hash);
}

export function getSessionId(): string {
  let sessionId = sessionStorage.getItem('session_id');
  if (!sessionId) {
    sessionId = `session_${Date.now()}_${Math.random().toString(36).substring(2)}`;
    sessionStorage.setItem('session_id', sessionId);
  }
  return sessionId;
}

export interface ABTestStats {
  variant: string;
  impressions: number;
  conversions: number;
  conversionRate: number;
}

export function calculateConversionRate(
  impressions: number,
  conversions: number
): number {
  return impressions > 0 ? (conversions / impressions) * 100 : 0;
}

export function calculateStatisticalSignificance(
  controlStats: ABTestStats,
  variantStats: ABTestStats
): { significant: boolean; confidence: number } {
  // Simple z-test for proportions
  const p1 = controlStats.conversions / controlStats.impressions;
  const p2 = variantStats.conversions / variantStats.impressions;
  const n1 = controlStats.impressions;
  const n2 = variantStats.impressions;

  const pooled = (controlStats.conversions + variantStats.conversions) / (n1 + n2);
  const se = Math.sqrt(pooled * (1 - pooled) * (1 / n1 + 1 / n2));
  const z = Math.abs(p1 - p2) / se;

  // Z-score to confidence level (approximate)
  const confidence = Math.min(99.9, (1 - Math.exp(-z * z / 2)) * 100);
  const significant = confidence >= 95;

  return { significant, confidence };
}
